import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineSegments,
  ShaderMaterial,
  Vector3,
  type Texture,
} from "three";
import { DateTime } from "luxon";

import { EARTH_RADIUS } from "./constants";
import { latLngToVec3 } from "./geo";
import { offsetAt } from "./tzrules";

// ============================================================
//  Live time-zone boundaries — drawn only where the two sides keep
//  different time right now.
//
//  public/data/zone-lines.json (scripts/build-zones.mjs) holds every
//  land boundary between two timezone-boundary-builder zones, with the
//  zone on each side. Poland | Kaliningrad is one such line: shown in
//  winter (UTC+1 | UTC+2), hidden in summer (both UTC+2). The geometry
//  is rebuilt when an offset changes.
//
//  Each fragment reads the same water mask the globe draws its coast
//  from and is dropped over sea, so a line never runs where the map
//  shows water, whatever the data's own idea of the coast.
// ============================================================

const VERT = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Direction → the globe's equirectangular UV (see latLngToVec3):
// u = (lng + 180) / 360, v = (lat + 90) / 180.
const FRAG = /* glsl */ `
  uniform sampler2D uWaterTex;
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec3 vDir;
  const float PI = 3.141592653589793;
  void main() {
    vec3 d = normalize(vDir);
    float u = fract(atan(d.z, -d.x) / (2.0 * PI));
    float v = asin(clamp(d.y, -1.0, 1.0)) / PI + 0.5;
    if (texture2D(uWaterTex, vec2(u, v)).r > 0.5) discard;
    gl_FragColor = vec4(uColor, uOpacity);
  }
`;

const DATA_URL = "/data/zone-lines.json";
// Just above the country borders (1.002), so a zone boundary that runs
// along a country border draws on top of it.
const LINE_RADIUS = EARTH_RADIUS * 1.0028;

interface Line {
  a: string;
  b: string;
  coords: number[];
}

export interface ZoneLines {
  mesh: LineSegments<BufferGeometry, ShaderMaterial>;
  /** Recompute live offsets; rebuilds when any changed. */
  update(now: DateTime): void;
  setStyle(rgb: [number, number, number], opacity: number): void;
}

export async function loadZoneLines(waterTex: Texture): Promise<ZoneLines> {
  const res = await fetch(DATA_URL);
  if (!res.ok) throw new Error(`zone-lines: ${res.status}`);
  const { lines } = (await res.json()) as { lines: Line[] };

  // Unit-sphere positions once; rebuilding only picks which lines show.
  const a = new Vector3();
  const b = new Vector3();
  const segs: Float32Array[] = lines.map((l) => {
    const out: number[] = [];
    for (let i = 0; i < l.coords.length - 2; i += 2) {
      const [lng1, lat1, lng2, lat2] = l.coords.slice(i, i + 4);
      if (Math.abs(lng2 - lng1) > 180) continue;
      latLngToVec3(lat1, lng1, LINE_RADIUS, a);
      latLngToVec3(lat2, lng2, LINE_RADIUS, b);
      out.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
    return new Float32Array(out);
  });

  const zones = [...new Set(lines.flatMap((l) => [l.a, l.b]))];
  const geo = new BufferGeometry();
  const material = new ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uWaterTex: { value: waterTex },
      uColor: { value: new Color(1, 1, 1) },
      uOpacity: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
  });
  const mesh = new LineSegments(geo, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  mesh.visible = false;

  let signature = "";
  function rebuild(now: DateTime): void {
    const ms = now.toMillis();
    const offset = new Map(zones.map((z) => [z, offsetAt(z, ms)]));
    const sig = zones.map((z) => offset.get(z)).join(",");
    if (sig === signature) return;
    signature = sig;
    const shown = segs.filter((_, i) => offset.get(lines[i].a) !== offset.get(lines[i].b));
    const positions = new Float32Array(shown.reduce((n, s) => n + s.length, 0));
    let at = 0;
    for (const s of shown) {
      positions.set(s, at);
      at += s.length;
    }
    geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  }

  rebuild(DateTime.utc());
  let lastMinute = -1;
  return {
    mesh,
    update(now) {
      const minute = Math.floor(now.toMillis() / 60000);
      if (minute === lastMinute) return;
      lastMinute = minute;
      rebuild(now);
    },
    setStyle(rgb, opacity) {
      material.uniforms.uColor.value.setRGB(...rgb);
      material.uniforms.uOpacity.value = opacity;
    },
  };
}
